import { useMemo } from 'react';
import { ROW_HEIGHT } from '../constants';
import { GroupSpan, TimelineViewModel } from '../types';
import { getZoomConfig, ZoomLevel } from '../utils/timeScale';

type Params = {
  startDate: Date;
  endDate: Date;
  rowCount: number;
  zoom: ZoomLevel;
};

const toDayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const useTimelineViewModel = ({
  startDate,
  endDate,
  rowCount,
  zoom,
}: Params): TimelineViewModel => {
  return useMemo(() => {
    const { columnsPerDay, columnWidth } = getZoomConfig(zoom);
    const HOURS_PER_COLUMN = 24 / columnsPerDay;

    // 1. Calendar days (range basis) — same for both zooms.
    const days: Date[] = [];
    const dayKeys: string[] = [];
    const cursor = new Date(startDate);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    while (cursor.getTime() <= end.getTime()) {
      const d = new Date(cursor);
      days.push(d);
      dayKeys.push(toDayKey(d));
      cursor.setDate(cursor.getDate() + 1);
    }

    // 2. Columns — each day subdivided into `columnsPerDay` blocks (monthly = 1).
    const columns: Date[] = [];
    const columnKeys: string[] = [];
    days.forEach((d) => {
      for (let k = 0; k < columnsPerDay; k += 1) {
        const c = new Date(d);
        c.setHours(k * HOURS_PER_COLUMN, 0, 0, 0);
        columns.push(c);
        columnKeys.push(`${toDayKey(d)}-${k}`);
      }
    });

    // 3. Top-header groups (in column-index space): months (monthly) or days (daily).
    const sameGroup = (a: Date, b: Date): boolean =>
      zoom === 'daily'
        ? isSameDay(a, b)
        : a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
    const groupSpans: GroupSpan[] = [];
    columns.forEach((c, i) => {
      const last = groupSpans[groupSpans.length - 1];
      if (last && sameGroup(last.anchor, c)) last.count += 1;
      else groupSpans.push({ anchor: c, startIdx: i, count: 1 });
    });

    const today = new Date();
    const todayIdx = days.findIndex((d) => isSameDay(d, today));

    return {
      days,
      dayKeys,
      columns,
      columnKeys,
      columnsPerDay,
      columnWidth,
      groupSpans,
      todayIdx,
      totalWidth: columns.length * columnWidth,
      totalBodyHeight: rowCount * ROW_HEIGHT,
      rowCount,
    };
  }, [startDate, endDate, rowCount, zoom]);
};

export default useTimelineViewModel;
